import type { User } from '../modules/platform/types';
import { USER_ROLES } from '../utils/constants';

const SELECTED_ROLE_KEY = 'tkm_selected_role';

/** Login UI historically stored lowercase tab keys (`student`); platform uses `STUDENT`. */
const LOGIN_TAB_TO_PLATFORM: Record<string, string> = {
  [USER_ROLES.STUDENT.toLowerCase()]: USER_ROLES.STUDENT,
  [USER_ROLES.TEACHER.toLowerCase()]: USER_ROLES.TEACHER,
  [USER_ROLES.PARENT.toLowerCase()]: USER_ROLES.PARENT,
  [USER_ROLES.SCHOOL_ADMIN.toLowerCase()]: USER_ROLES.SCHOOL_ADMIN,
  school_admin: USER_ROLES.SCHOOL_ADMIN,
  dev_admin: USER_ROLES.DEV_TEAM,
  [USER_ROLES.DEV_TEAM.toLowerCase()]: USER_ROLES.DEV_TEAM,
};

function normalizeStoredRoleKey(stored: string | null): string | null {
  if (!stored) return null;
  const trimmed = stored.trim();
  if (!trimmed) return null;
  if (LOGIN_TAB_TO_PLATFORM[trimmed.toLowerCase()]) {
    return LOGIN_TAB_TO_PLATFORM[trimmed.toLowerCase()];
  }
  return trimmed;
}

/** Map login tab key or legacy localStorage value to `USER_ROLES.*` when recognized. */
export function normalizeLoginRoleSelection(shellKey: string | null | undefined): string | null {
  return normalizeStoredRoleKey(shellKey?.trim() || null);
}

/**
 * Pick which role to send as `X-Role` for gateway / BFF routing.
 * Prefer the login-shell selection when it is still one of the user's roles.
 */
export function pickRoleForApiHeaders(user: User | null | undefined): string | null {
  if (!user?.roles?.length) return null;
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(SELECTED_ROLE_KEY);
  } catch {
    stored = null;
  }
  const normalized = normalizeStoredRoleKey(stored);
  if (normalized && user.roles.includes(normalized as (typeof user.roles)[number])) {
    return normalized;
  }
  return user.roles[0] ?? null;
}

/**
 * Persist identity headers after `getCurrentUser()` (or login refresh).
 * Keeps `X_ROLE` aligned with `tkm_selected_role` when valid.
 */
export function applyRoleHeadersFromUser(user: User | null): void {
  try {
    const role = pickRoleForApiHeaders(user);
    const tenantId = user?.memberships?.[0]?.school_id || '';
    if (role) localStorage.setItem('X_ROLE', role);
    else localStorage.removeItem('X_ROLE');
    if (tenantId) localStorage.setItem('X_TENANT_ID', tenantId);
    else localStorage.removeItem('X_TENANT_ID');
    if (user?.id) localStorage.setItem('X_USER_ID', user.id);
    else localStorage.removeItem('X_USER_ID');
  } catch {
    /* ignore quota / private mode */
  }
}

export function authDebugEnabled(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_AUTH_DEBUG === '1';
}

/** Structured client-side auth trace (enable with VITE_AUTH_DEBUG=1 or in DEV). */
export function logAuthDebug(event: string, detail: Record<string, unknown> = {}): void {
  if (!authDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.info('[AUTH]', JSON.stringify({ event, ts: new Date().toISOString(), ...detail }));
}
